import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faX, faSearch, faPlus, faMinus, faCheck, faBox } from '@fortawesome/free-solid-svg-icons';

const AssignProductsModal = ({ supplier, category, onClose, onAssignmentComplete, existingAssignments, CategoryProducts }) => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const imageUrl = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || '';

  useEffect(() => {
    // Filter products by category
    setProducts(CategoryProducts);
   console.log(CategoryProducts);
    // Pre-select already assigned products
    if (existingAssignments) {
      const preSelected = {};
      existingAssignments.assigned_products.forEach(assigned => {
        preSelected[assigned._id] = {
          selected: true,
          supplier_price: assigned.supplier_price,
          moqs: assigned.moqs || []
        };
      });
      setSelectedProducts(preSelected);
    }
  }, [category._id, existingAssignments]);


  const filteredProducts = useMemo(() => {
    return products.filter(product =>
      product.product_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [products, searchTerm]);
 

  const handleProductSelect = (productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: prev[productId]?.selected 
        ? { ...prev[productId], selected: false }
        : { 
            selected: true, 
            supplier_price: products.find(p => p._id === productId)?.selling_price || 0,
            moqs: []
          }
    }));
  };

  const handlePriceChange = (productId, price) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        supplier_price: parseFloat(price) || 0
      }
    }));
  };
   const handleSKUChange = (productId, SKU) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        SKU_Code: SKU || 0
      }
    }));
  };

  const handleMOQChange = (productId, moqIndex, field, value) => {
    setSelectedProducts(prev => {
      const updated = { ...prev };
      if (!updated[productId].moqs) updated[productId].moqs = [];
      if (!updated[productId].moqs[moqIndex]) {
        updated[productId].moqs[moqIndex] = { quantity: 0, selling_price: 0 };
      }
      updated[productId].moqs[moqIndex][field] = field === 'selling_price' ? parseFloat(value) || 0 : parseInt(value) || 0;
      return updated;
    });
  };

  const addMOQ = (productId) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        moqs: [...(prev[productId].moqs || []), { min_quantity: 0, max_quantity:0, price: 0 }]
      }
    }));
  };

  const removeMOQ = (productId, moqIndex) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        moqs: prev[productId].moqs.filter((_, index) => index !== moqIndex)
      }
    }));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts({});
    } else {
      const allSelected = {};
      filteredProducts.forEach(product => {
        allSelected[product._id] = {
          selected: true,
          supplier_price: product.selling_price,
          moqs: []
        };
      });
      setSelectedProducts(allSelected);
    }
    setSelectAll(!selectAll);
  };

  const handleAssign = async () => {
    setIsLoading(true);
    
    const assignedProducts = Object.entries(selectedProducts)
      .filter(([_, data]) => data.selected)
      .map(([productId, data]) => {
        const product = products.find(p => p._id === productId);
        console.log(data);
        return {
          product_id: productId,
          product_name: product.product_name,
          selling_price: data.supplier_price,
          SKU_Code: data.SKU_Code,
          original_price: product.selling_price,
          moqs: data.moqs || [],
          assigned_date: new Date().toISOString().split('T')[0],
          is_active: true
        };
      });
   console.log(assignedProducts);
    const assignment = {
      _id: existingAssignments?._id || `assign_${Date.now()}`,
      vendor_id: supplier._id,
      product_category_id: category._id,
      assigned_products: assignedProducts
    };

    // Simulate API call
    setTimeout(() => {
      onAssignmentComplete(assignment);
      setIsLoading(false);
    }, 1000);
  };

  const selectedCount = Object.values(selectedProducts).filter(data => data.selected).length;

  return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
    <div className="bg-white rounded-lg shadow-lg w-full max-w-7xl h-[95vh] sm:h-[90vh] flex flex-col">
      {/* Header */}
      <div className="border-b px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-start sm:items-center">
        <div className="flex-1 min-w-0 pr-3">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
            Assign Products to Supplier
          </h2>
          <div className="text-xs sm:text-sm text-gray-600 mt-1 space-y-1 sm:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <span>
                Supplier: <span className="font-medium">{supplier.name}</span>
              </span>
              <span className="hidden sm:inline">|</span>
              <span>
                Category: <span className="font-medium">{category.product_category_name}</span>
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
        >
          <FontAwesomeIcon icon={faX} className="text-base sm:text-lg" />
        </button>
      </div>

      {/* Search and Controls */}
      <div className="border-b p-3 sm:p-4 bg-gray-50">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-between sm:items-center">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <div className="relative flex-1 sm:flex-initial">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <button
              onClick={handleSelectAll}
              className="flex items-center justify-center sm:justify-start gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
            >
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="rounded"
              />
              <span className="hidden sm:inline">Select All ({filteredProducts.length})</span>
              <span className="sm:hidden">All ({filteredProducts.length})</span>
            </button>
          </div>
          <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-right">
            {selectedCount} of {filteredProducts.length} products selected
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="flex-1 overflow-auto">
        {filteredProducts.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredProducts.map(product => {
              const isSelected = selectedProducts[product._id]?.selected || false;
              const productData = selectedProducts[product._id] || {};
              
              return (
                <div key={product._id} className={`p-3 sm:p-4 ${isSelected ? 'bg-blue-50' : 'bg-white'}`}>
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Checkbox */}
                    <div className="flex items-center pt-2 flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleProductSelect(product._id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>

                    {/* Product Image */}
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      {product.product_image ? (
                        <img
                          src={`${imageUrl}${product.product_image}`}
                          alt={product.product_name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <span className="text-gray-400 text-xs text-center">No Image</span>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">
                            {product.product_name}
                          </h4>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">
                            {(() => {
                              try {
                                if (product.specifications) {
                                  const specs = JSON.parse(product.specifications);
                                  if (Array.isArray(specs) && specs.length > 0) {
                                    return specs
                                      .map(spec =>
                                        Object.entries(spec)
                                          .filter(([key, value]) => key && value)
                                          .map(([key, value]) => `${key}: ${value}`)
                                          .join(', ')
                                      )
                                      .filter(Boolean)
                                      .join(' | ');
                                  }
                                }
                                return '';
                              } catch (error) {
                                return product.specifications || '';
                              }
                            })()}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-500">
                            {product.SKU && <span>SKU: {product.SKU}</span>}
                            {product.uom?.UOM && <span>UOM: {product.uom?.UOM}</span>}
                            {product.selling_price && <span>Price: ₹{product.selling_price}</span>}
                          </div>
                        </div>
                      </div>

                      {/* Pricing and MOQ Section - Only show if selected */}
                      {isSelected && (
  <div className="mt-4 p-3 sm:p-4 bg-white rounded-lg border">
    <div className="space-y-4">
      {/* Supplier Price and SKU Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        <div className="lg:col-span-1">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
            Supplier Price *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">₹</span>
            <input
              type="number"
              value={productData.supplier_price || ''}
              onChange={(e) => handlePriceChange(product._id, e.target.value)}
              className="pl-8 pr-3 py-2 w-full max-w-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Enter price"
            />
          </div>
        </div>
        
        <div className="lg:col-span-1">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
            Item SKU *
          </label>
          <input
            value={productData.SKU_Code || ''}
            onChange={(e) => handleSKUChange(product._id, e.target.value)}
            className="px-3 py-2 w-full max-w-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Enter SKU"
          />
        </div>

        {/* Add MOQ Button - Aligned on large screens */}
        <div className="lg:col-span-2 flex items-end justify-start lg:justify-end">
          <button
            onClick={() => addMOQ(product._id)}
            className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm flex items-center gap-1 bg-blue-50 px-3 py-2 rounded hover:bg-blue-100 transition-colors whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faPlus} className="text-xs" />
            Add MOQ
          </button>
        </div>
      </div>

      {/* MOQ Section */}
      <div>
        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-3">
          Minimum Order Quantities (MOQ)
        </label>
        
        <div className="space-y-2">
          {(productData.moqs || []).map((moq, index) => (
            <div key={index} className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2 lg:gap-3 p-2 sm:p-3 bg-gray-50 rounded border">
              <div className="grid grid-cols-3 lg:grid-cols-3 gap-2 lg:gap-3 flex-1 lg:max-w-md">
                {/* Min Quantity */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Min Qty</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={moq.min_quantity || ''}
                    onChange={(e) => handleMOQChange(product._id, index, 'min_quantity', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {/* Max Quantity */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Max Qty</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={moq.max_quantity || ''}
                    onChange={(e) => handleMOQChange(product._id, index, 'max_quantity', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Price</label>
                  <div className="relative">
                    <span className="absolute left-1 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">₹</span>
                    <input
                      type="number"
                      placeholder="0"
                      value={moq.selling_price || ''}
                      onChange={(e) => handleMOQChange(product._id, index, 'selling_price', e.target.value)}
                      className="pl-3 pr-1 py-1 w-full border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Remove Button */}
              <div className="flex justify-center lg:justify-start">
                <button
                  onClick={() => removeMOQ(product._id, index)}
                  className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                  title="Remove MOQ"
                >
                  <FontAwesomeIcon icon={faMinus} className="text-xs" />
                </button>
              </div>
            </div>
          ))}

          {(!productData.moqs || productData.moqs.length === 0) && (
            <div className="text-center py-3 bg-gray-50 rounded border-dashed border-2 border-gray-300">
              <p className="text-xs sm:text-sm text-gray-500">No MOQ defined</p>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-center px-4">
              <div className="text-gray-400 text-3xl sm:text-4xl mb-4">
                <FontAwesomeIcon icon={faSearch} />
              </div>
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No products found</h3>
              <p className="text-sm sm:text-base text-gray-600">Try adjusting your search criteria</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t p-3 sm:p-4 bg-gray-50">
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
            {selectedCount} product{selectedCount !== 1 ? 's' : ''} selected for assignment
          </div>
          <div className="flex gap-2 sm:gap-3">
                       <button
              onClick={onClose}
              className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={selectedCount === 0 || isLoading}
              className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 text-sm rounded-md flex items-center justify-center gap-2 ${
                selectedCount > 0 && !isLoading
                  ? 'bg-[#f7525a] text-white hover:bg-[#e04850]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span className="hidden sm:inline">Assigning...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} />
                  <span className="hidden sm:inline">Assign Products ({selectedCount})</span>
                  <span className="sm:hidden">Assign ({selectedCount})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

};

export default AssignProductsModal;

